'use client'

import { useEffect, useState, Suspense } from 'react'
import { usePathname } from 'next/navigation'
import { motion, AnimatePresence } from 'framer-motion'

/* ─── Inner component that uses usePathname ─────────────────────────────── */
function PageTransitionInner({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()

  // Skip animation entirely for admin pages
  if (pathname.startsWith('/admin')) {
    return <>{children}</>
  }

  return (
    <>
      <AnimatePresence mode="wait">
        <motion.div
          key={pathname + '-overlay'}
          initial={{ y: '100%' }}
          animate={{ y: [null, '0%', '-100%'] }}
          transition={{
            duration: 0.7,
            ease: [0.76, 0, 0.24, 1],
            times: [0, 0.5, 1],
          }}
          className="fixed inset-0 z-[999] pointer-events-none flex items-center justify-center"
          style={{ backgroundColor: '#FDF6EC' }}
        >
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: [0, 1, 1, 0] }}
            transition={{ duration: 0.7, times: [0, 0.25, 0.75, 1] }}
            className="font-playfair font-bold text-[#8B4513] text-2xl md:text-3xl select-none"
            style={{ fontFamily: 'var(--font-playfair), Playfair Display, Georgia, serif' }}
          >
            Bakkerij Voortman
          </motion.p>
        </motion.div>
      </AnimatePresence>

      <AnimatePresence mode="wait">
        <motion.div
          key={pathname}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.45, ease: [0.25, 0.1, 0.25, 1], delay: 0.1 }}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </>
  )
}

/* ─── Public export wrapped in Suspense ─────────────────────────────────── */
export default function PageTransition({ children }: { children: React.ReactNode }) {
  // Guard: only render on client after hydration to avoid SSR/CSR mismatch
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  if (!mounted) {
    // During SSR / first paint render children directly without animations
    return <>{children}</>
  }

  return (
    <Suspense fallback={<>{children}</>}>
      <PageTransitionInner>{children}</PageTransitionInner>
    </Suspense>
  )
}
